import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Notification} from '@flixbus/honeycomb-react';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {removeEditScreenError} from '@/Pages/ProductionCost/store/slice';
import {BasicError} from '@/Pages/ProductionCost/store/types';
import {
  BusInputValidationErrorExtensions,
  ScheduleInputValidationErrorExtensions
} from '../../types';
import {
  geBusInputValidationErrorMessage,
  getScheduleInputValidationErrorMessage
} from './ProductionCostErrorNotification.utils';

const ProductionCostErrorNotification: React.FC = () => {
  const busValues = useAppSelector(
    ({productionCost}) => productionCost.busValues
  );
  const loading = useAppSelector(({productionCost}) => productionCost.loading);
  const calculateProductionCostErrors = useAppSelector(
    ({productionCost}) => productionCost.serverErrors.calculateProductionCost
  );
  const dispatch = useAppDispatch();

  const getErrorMessage = useCallback(
    (
      error:
        | BusInputValidationErrorExtensions
        | ScheduleInputValidationErrorExtensions
        | BasicError
    ) => {
      if ('invalidDays' in error) {
        return getScheduleInputValidationErrorMessage(error);
      } else if ('vehicleId' in error) {
        const vehicleSettings = busValues?.find(
          ({vehicleId}) => vehicleId === error.vehicleId
        );

        if (!vehicleSettings) {
          const fallbackMessage = `Missing bus values for vehicle id: ${error.vehicleId}`;
          console.error(fallbackMessage);
          return fallbackMessage;
        }

        return geBusInputValidationErrorMessage(error, vehicleSettings);
      } else {
        return error.errorMessage ?? 'An unexpected error occurred';
      }
    },
    [busValues]
  );

  const errorsList = useMemo(() => {
    if (!calculateProductionCostErrors) return [];
    return calculateProductionCostErrors.other.map(error => ({
      id: error.id,
      errorMessage: getErrorMessage(error)
    }));
  }, [calculateProductionCostErrors, getErrorMessage]);

  // Scroll to the top only when loading switches from true to false and errors exist
  const previousLoading = useRef<boolean>(loading);
  useEffect(() => {
    if (previousLoading.current && !loading && errorsList.length > 0) {
      window.scrollTo(0, 0);
    }

    // Update the ref value after comparison
    previousLoading.current = loading;
  }, [loading, errorsList]);

  const removeError = useCallback(
    (errorId: string) => dispatch(removeEditScreenError(errorId)),
    [dispatch]
  );

  if (!errorsList.length) return null;

  return (
    <>
      {errorsList.map(error => (
        <Notification
          key={`calculation-error-${error.id}`}
          appearance="danger"
          closeProps={{
            'aria-label': 'Close',
            onClick: () => removeError(error.id)
          }}
        >
          {error.errorMessage}
        </Notification>
      ))}
    </>
  );
};

export default React.memo(ProductionCostErrorNotification);
